import React from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';

import { useModal } from '../../hooks/useModal';
import { useApi } from '../../hooks/useApi';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../contexts/AuthContext';
import { replaceLocation, getBaseRoute } from '../../utility';

import { SelectOrganizationModal } from './SelectOrganizationModal';
import Loading from '../../components/Loading';

import suomifiLogo from '../../../public/assets/img/suomifi_logo_RGB.png';
import hakaLogo from '../../../public/assets/img/Haka_login_vaaka.jpg';
import ainiLogoAndText from '../../../public/assets/img/Eduplan_Aini_logo_iconAndText_orangeAndBlack.svg';
import eduixLogo from '../../../public//assets/img/eduix_logo_200x82_orange.png';

import styles from './styles.module.scss';

const identificationServices = {
  haka: {
    title: 'login.haka.identification',
    img: {
      src: hakaLogo,
      alt: 'Haka logo',
    }
  },
  tunnistaudu: {
    title: 'login.suomifi.identification',
    img: {
      src: suomifiLogo,
      alt: 'Suomi.fi logo',
      style: {
        width: '80%',
        margin: '1rem'
      }
    }
  }
};

export default function Identification() {
  const { isLoggedIn, user, role } = useAuth();
  const { locales, setLocale, localize } = useLocalization();
  const modal = useModal();

  const { data: loginOptions, loading } = useApi('/auth/login', {
    fetchOnMount: !isLoggedIn
  });

  if (isLoggedIn) {
    return <Navigate to={getBaseRoute(user, role) || '/'} replace />;
  }

  const handleSelectService = (service) => {
    if (!service) return;

    if (typeof service === 'string') {
      replaceLocation(service);
    }

    if (Array.isArray(service)) {
      modal.open({
        content: (
          <SelectOrganizationModal
            tenants={service}
            handleClose={() => modal.close()}
          />
        ),
        options: {
          size: 'md'
        }
      });
    }
  };

  return (
    <Container className={styles.loginPage}>
      <Row>
        <Col
          sm={{ span: 12 }}
          md={{ span: 8, offset: 2 }}
          lg={{ span: 6, offset: 3 }}
          xl={{ span: 6, offset: 3 }}>

          {loading && <Loading />}

          <Card className={`${styles.card} my-3`}>
            <Card.Header
              className='p-4'
              style={{
                border: 0,
                backgroundColor: 'white',
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
              <img
                style={{ width: '15rem', height: 'auto' }}
                aria-hidden='true'
                src={ainiLogoAndText}
                alt='Aini logo and name'
              />
              <h1 className='d-flex text-center m-0 flex-wrap'>
                {localize('login.identify')}
              </h1>
            </Card.Header>

            <Card.Body>
              <Col>
                <IdentificationServices
                  onSelect={handleSelectService}
                  loginOptions={loginOptions}
                />
              </Col>
              <hr />
              <div className='d-flex m-0 my-0 justify-content-center'>
                {window._env?.urls?.appDetails && (
                  <a
                    className='btn btn-link'
                    target='_blank'
                    href={window._env.urls.appDetails}>
                    {localize('login.link.appDetails')}
                  </a>
                )}
                {window._env?.urls?.eduix && (
                  <a
                    className='btn btn-link'
                    target='_blank'
                    href={window._env.urls.eduix}
                    aria-label='Eduix'>
                    <img
                      style={{ maxHeight: '30px', marginTop: '-10px' }}
                      src={eduixLogo}
                      alt='Eduix'
                    />
                  </a>
                )}
              </div>
            </Card.Body>

            {locales.length > 0 && (
              <Card.Footer className={styles.cardFooter}>
                {locales.map((locale) => (
                  <Button
                    key={locale}
                    onClick={() => setLocale(locale)}
                    variant='link'
                    aria-label={localize(`common.btn.locale.${locale}`)}
                    tabIndex={0}>
                    {localize(`common.btn.locale.${locale}`)}
                  </Button>
                ))}
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

const IdentificationServices = ({ onSelect, loginOptions }) => {
  const { localize } = useLocalization();

  if (!loginOptions) return null;

  return (
    <>
      {Object.entries(loginOptions).map(([key, value]) => {
        if (!value || (Array.isArray(value) && !value.length)) return null;
        const service = identificationServices[key];
        if (!service) return null;

        return (
          <Card
            key={key}
            className={`${styles.identificationServiceCard}`}
            role='button'
            tabIndex={0}
            aria-labelledby={service.title}
            onClick={() => onSelect?.(value)}
            onKeyUp={(e) => e.key === 'Enter' && onSelect?.(value)}>
            <Card.Header className={styles.loginIdentificationCard}>
              <img
                style={{ width: '100%', height: 'auto' }}
                aria-hidden='true'
                {...service.img}
              />
            </Card.Header>
            <Card.Body>
              <Card.Title
                className='d-flex justify-content-center'
                id={service.title}>
                <strong>{localize(service.title)}</strong>
              </Card.Title>
            </Card.Body>
          </Card>
        );
      })}
    </>
  );
};
